/**
 * `<MobileDrawer/>` — the lead / co-lead mobile navigation drawer
 * (#966 P3 / #970).
 *
 * On phone widths the **member** role gets the bottom-tab bar
 * (`<MemberTabBar/>`, `web/src/memberapp/MemberTabBar.tsx`, #968). The
 * **lead / co_lead** (and the no-role
 * fall-through) instead get a hamburger → left-anchored slide-in drawer
 * that hosts the lead nav: the `leadNavLinks` items (Cycles · Members ·
 * Setup) plus the module switcher (Roster ▾) and the workspace
 * switcher.
 *
 * Built on the same Radix dialog foundation as `ui/BottomSheet` — so it
 * gets focus-trap, escape-to-close, scroll-lock, the portal, and correct
 * ARIA roles for free — but anchored to the left edge (`inset-y-0
 * left-0`) with the `slide-in-left` transition instead of `slide-up`.
 *
 * Desktop is unaffected: the whole surface is `md:hidden` (the desktop
 * inline nav strip + the `md`-and-up nav in `AppShell` take over), and
 * the AppShell only mounts the hamburger + drawer on the lead/no-member
 * mobile branch. The member bottom-tab path is untouched.
 *
 * The nav links are passed in (already derived by AppShell via
 * `deriveNavLinks`) rather than re-derived here, so the drawer never
 * drifts from the desktop strip + the `team_kind === "tasks"` seam is
 * honoured upstream without this component having to know about it.
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "../../lib/cn";
import { Logo } from "../Logo";
import { WorkspaceSwitcher } from "../WorkspaceSwitcher";
import { ModuleSwitcher } from "./ModuleSwitcher";
import { type NavLinkSpec, pickActiveNavLink } from "./navLinks";

export interface MobileDrawerProps {
  /** Open/close state. AppShell owns it (the hamburger toggles it). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The role-derived nav links (Cycles · Members · Setup for leads;
   *  Map · Members · Settings for a tasks team). Passed in so the
   *  drawer stays in sync with the desktop strip and never re-derives. */
  navLinks: NavLinkSpec[];
  /** Whether to render the workspace switcher inside the drawer (mirrors
   *  the AppShell top-bar gate: switcher enabled + signed in). */
  showWorkspaceSwitcher: boolean;
  /** #2048 — mirrors the top bar: the module switcher only for an account
   *  with more than one kind of team. Defaults on for older callers. */
  showModuleSwitcher?: boolean;
}

export function MobileDrawer({
  open,
  onOpenChange,
  navLinks,
  showWorkspaceSwitcher,
  showModuleSwitcher = true,
}: MobileDrawerProps) {
  const { pathname } = useLocation();
  const activeNavTo = pickActiveNavLink(navLinks, pathname);

  // Close the drawer whenever navigation lands a new route (mirrors the
  // member "More" sheet's close-on-navigate behaviour).
  // biome-ignore lint/correctness/useExhaustiveDependencies: close-on-navigate keys off pathname only.
  useEffect(() => onOpenChange(false), [pathname]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="motion-scrim fixed inset-0 z-50 bg-black/40 backdrop-blur-sm md:hidden" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex w-[84%] max-w-xs flex-col overflow-y-auto",
            // #2172 — slides in from the left edge and back out on close.
            "motion-drawer-left border-r border-border bg-bg-elevated pb-safe-bottom shadow-lg",
            "focus:outline-none md:hidden",
          )}
          aria-describedby={undefined}
          data-testid="mobile-drawer"
        >
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          {/* Header — brand + close. */}
          <div className="flex items-center justify-between border-b border-border px-4 pt-safe-top">
            <div className="py-3">
              <Logo />
            </div>
            <DialogPrimitive.Close
              className="flex min-h-tap min-w-tap items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>

          {/* Switchers — module (Roster ▾) + workspace. Same components
              the desktop top-bar uses; they portal their own dropdowns. */}
          <div className="flex flex-col gap-2 border-b border-border px-3 py-3">
            {showModuleSwitcher ? <ModuleSwitcher /> : null}
            {showWorkspaceSwitcher ? <WorkspaceSwitcher /> : null}
          </div>

          {/* Lead nav links — the same `leadNavLinks` set as the desktop
              strip. Each is a comfortable touch target. */}
          <nav
            className="flex flex-col gap-1 px-3 py-3"
            data-testid="mobile-drawer-nav"
            aria-label="Primary"
          >
            {navLinks.map((link) => {
              // #2172 — one current entry (`pickActiveNavLink`), the same
              // pick as the desktop strip.
              const current = link.to === activeNavTo;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "flex min-h-tap items-center rounded-lg px-3 text-sm",
                    link.focal ? "font-semibold" : "font-medium",
                    current
                      ? "bg-bg-subtle text-fg"
                      : "text-fg-muted hover:bg-bg-subtle hover:text-fg",
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * `useMobileDrawer` — convenience open-state hook for the AppShell
 * hamburger. Kept tiny + co-located so the AppShell wiring reads as a
 * one-liner; the drawer itself is a fully controlled component.
 */
export function useMobileDrawer(): {
  open: boolean;
  setOpen: (open: boolean) => void;
} {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
